import React, {
	type PropsWithChildren,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import DropdownMenu, { type CustomTriggerProps } from '@atlaskit/dropdown-menu';
import type { Placement } from '@atlaskit/dropdown-menu/types';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import PowerCableIcon from '@atlaskit/icon-lab/core/power-cable';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import GlobeIcon from '@atlaskit/icon/core/globe';
import Image from '@atlaskit/image';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { MenuGroup } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useConnectors } from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { useEnsureConnectorsLoaded } from '@atlassian/conversation-assistant-store/controllers/connectors-store/use-ensure-connectors-loaded';
import { GhostIconWave } from '@atlassian/rovo-platform-ui-components/primitives/GhostIconWave';

import { RovoLogoFromLogo } from '../../../../common/ui/icons/RovoLogoFromLogo';
import { RovoLogoForAppSource } from '../../../../common/ui/RovoLogoForAppSource';
import { getAllConnectedApps } from '../../../../common/utils/get-all-connected-apps';
import { is3pApp } from '../../../../common/utils/is-3p-app';
import {
	type FiltererdOrgKnowledge,
	type OrgKnowledge,
	type RovoApp,
	type SourceMode,
} from '../../../../controllers/chat-mode-store/types';
import { useChatModeStore } from '../../../../controllers/chat-mode-store/useChatModeStore';
import { useChatModeStoreActions } from '../../../../controllers/chat-mode-store/useChatModeStoreActions';
import { type MentionAgentsSpotlightPostOfficeMessage } from '../../MentionAgentsSpotlightPostOfficeMessage';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { FilterByAppsSelector } from '../app-filter';

import { AgentSourceControlSpotlight } from './AgentSourceControlSpotlight';
import { buildGhostIconNodes } from './ghost-icons/buildGhostIconNodes';
import { useGhostIconsPillState } from './ghost-icons/useGhostIconsPillState';
import { useGhostRenderCoordination } from './ghost-icons/useGhostRenderCoordination';
import { HeadlessProbeRovoChatSourcesPostOfficePlacement } from './headless-probe-rovo-chat-sources-post-office-placement';
import { messages } from './messages';

const ClientSideOnly = ({ children }: PropsWithChildren): React.JSX.Element | null =>
	typeof window !== 'undefined' ? <>{children}</> : null;

const styles = cssMap({
	rovoLogoWrapper: {
		height: '16px',
		width: '16px',
		borderRadius: token('radius.xsmall'),
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		overflow: 'hidden',
	},
	placeholderLogo: {
		height: '16px',
		width: '16px',
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.accent.blue.subtle'),
	},
	popupContainer: {
		paddingTop: token('space.075'),
		paddingBottom: token('space.100'),
	},
	pillPressable: {
		backgroundColor: 'transparent',
		paddingBlock: token('space.0'),
	},
	pillPressableDisabled: {
		backgroundColor: 'transparent',
		cursor: 'not-allowed',
		opacity: token('opacity.disabled'),
		paddingBlock: token('space.0'),
	},
	pillContainer: {
		display: 'flex',
		alignItems: 'center',
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		paddingBlock: token('space.075'),
		paddingInline: token('space.150'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	pillContainerSelected: {
		display: 'flex',
		alignItems: 'center',
		borderRadius: token('radius.large'),
		borderColor: token('color.border.selected'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.150'),
	},
	iconItem: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '20px',
		height: '20px',
	},
	remainingCount: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 'auto',
	},
	spotlightWrapper: {
		position: 'relative',
		display: 'inline-flex',
	},
	spotlightContainer: {
		width: '0px',
		height: '0px',
		overflow: 'visible',
	},
});

export const FilteredAppsTag = ({
	isDisabled,
	chatModeKey,
	assistanceServiceParams,
	hideLabel,
	webSearchConfigEnabled,
	onFilteredAppsChange,
	onWebSearchToggle,
	onOrgKnowledgeToggle,
	contentContext = 'global',
	enableAppsDiscoveryUi,
	showSourceModeToggle,
	shouldRenderInPortal,
	disabledTooltip,
	showAgentSourceControlSpotlight = false,
	onDismissAgentSourceControlSpotlight,
	sourceControlPostOfficeMessage,
}: {
	isDisabled: boolean;
	chatModeKey: string;
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	hideLabel?: boolean;
	webSearchConfigEnabled: boolean;
	onFilteredAppsChange?: (apps: RovoApp[]) => void;
	onWebSearchToggle?: (enabled: boolean) => void;
	onOrgKnowledgeToggle?: (enabled: boolean) => void;
	contentContext?: string;
	/**
	 * When true, opts the surface in to apps discovery UI changes. The app
	 * filter applies the limited list and Discovery tab destination only when
	 * the side nav modal is available. Defaults to off so that surfaces which
	 * haven't been audited keep the existing behaviour.
	 */
	enableAppsDiscoveryUi?: boolean;
	showSourceModeToggle?: boolean;
	shouldRenderInPortal?: boolean;
	/**
	 * Overrides the pill's default tooltip. Used when the sources pill is shown
	 * but disabled (e.g. in an agentic conversation) to explain why the Rovo
	 * source controls do not apply for the current turn.
	 */
	disabledTooltip?: string;
	showAgentSourceControlSpotlight?: boolean;
	onDismissAgentSourceControlSpotlight?: (source?: 'cross-icon' | 'understood-button') => void;
	sourceControlPostOfficeMessage?: MentionAgentsSpotlightPostOfficeMessage;
}): React.JSX.Element | null => {
	return (
		<FilteredAppsTagNew
			isDisabled={isDisabled}
			chatModeKey={chatModeKey}
			assistanceServiceParams={assistanceServiceParams}
			hideLabel={hideLabel}
			webSearchConfigEnabled={webSearchConfigEnabled}
			onFilteredAppsChange={onFilteredAppsChange}
			onWebSearchToggle={onWebSearchToggle}
			onOrgKnowledgeToggle={onOrgKnowledgeToggle}
			contentContext={contentContext}
			enableAppsDiscoveryUi={enableAppsDiscoveryUi}
			showSourceModeToggle={showSourceModeToggle}
			shouldRenderInPortal={shouldRenderInPortal}
			disabledTooltip={disabledTooltip}
			showAgentSourceControlSpotlight={showAgentSourceControlSpotlight}
			onDismissAgentSourceControlSpotlight={onDismissAgentSourceControlSpotlight}
			sourceControlPostOfficeMessage={sourceControlPostOfficeMessage}
		/>
	);
};

type FilteredAppsTagProps = {
	isDisabled: boolean;
	chatModeKey: string;
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	hideLabel?: boolean;
	webSearchConfigEnabled: boolean;
	onFilteredAppsChange?: (apps: RovoApp[]) => void;
	onWebSearchToggle?: (enabled: boolean) => void;
	onOrgKnowledgeToggle?: (enabled: boolean) => void;
	contentContext?: string;
	enableAppsDiscoveryUi?: boolean;
	showSourceModeToggle?: boolean;
	shouldRenderInPortal?: boolean;
	disabledTooltip?: string;
	showAgentSourceControlSpotlight?: boolean;
	onDismissAgentSourceControlSpotlight?: (source?: 'cross-icon' | 'understood-button') => void;
	sourceControlPostOfficeMessage?: MentionAgentsSpotlightPostOfficeMessage;
};

const FilteredAppsTagNew = ({
	isDisabled,
	chatModeKey,
	assistanceServiceParams,
	hideLabel: _hideLabel,
	webSearchConfigEnabled,
	onFilteredAppsChange,
	onWebSearchToggle,
	onOrgKnowledgeToggle,
	contentContext = 'global',
	enableAppsDiscoveryUi,
	showSourceModeToggle,
	shouldRenderInPortal,
	disabledTooltip,
	showAgentSourceControlSpotlight = false,
	onDismissAgentSourceControlSpotlight,
	sourceControlPostOfficeMessage,
}: FilteredAppsTagProps): React.JSX.Element | null => {
	useEnsureConnectorsLoaded({ assistanceServiceParams });
	const { setOrgKnowledge, setSourceMode, setWebSearchEnabled } = useChatModeStoreActions();
	const { orgKnowledge, sourceMode, webSearchEnabled } = useChatModeStore(chatModeKey)[0];
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [connectors] = useConnectors();
	const connectedThirdPartyAppCount = useMemo(
		() =>
			Object.values(connectors).filter(
				(connector) => connector.isUserOAuthed && is3pApp(connector.friendlyName),
			).length,
		[connectors],
	);
	const {
		ghostConnectors,
		showGhostIcons: shouldRenderGhostIcons,
		isGhostIconsPending,
		shouldAnimate,
		oauthSource,
		handleSourcesPillClick,
		handleGhostConnectClick,
		reportRenderBlocked,
	} = useGhostIconsPillState({
		assistanceServiceParams,
		contentContext,
		connectedThirdPartyAppCount,
		sendAnalyticsEvent,
	});

	useEffect(() => {
		const handlePreferenceChange = (event: Event) => {
			if (event instanceof CustomEvent && typeof event.detail?.enabled === 'boolean') {
				setWebSearchEnabled(chatModeKey, event.detail.enabled);
			}
		};

		const unbind = bind(globalThis, {
			type: 'rovo-chat-web-search-preference-changed',
			listener: handlePreferenceChange,
		});

		return unbind;
	}, [chatModeKey, setWebSearchEnabled]);

	const allConnectedApps = useMemo(() => getAllConnectedApps(connectors), [connectors]);

	const hasInitializedAppsRef = useRef(false);
	useEffect(() => {
		if (
			!hasInitializedAppsRef.current &&
			allConnectedApps.length > 0 &&
			orgKnowledge !== 'disabled' &&
			orgKnowledge.filteredApps.length === 0
		) {
			hasInitializedAppsRef.current = true;
			setOrgKnowledge(chatModeKey, { filteredApps: allConnectedApps });
		}
	}, [allConnectedApps, chatModeKey, orgKnowledge, setOrgKnowledge]);

	const filteredApps = useMemo(() => {
		if (orgKnowledge === 'disabled') {
			return [];
		}
		if (orgKnowledge.filteredApps && orgKnowledge.filteredApps.length > 0) {
			const selectedSources = new Set(orgKnowledge.filteredApps.map((app) => app.source));
			return allConnectedApps.filter((app) => selectedSources.has(app.source));
		}
		return allConnectedApps;
	}, [orgKnowledge, allConnectedApps]);
	const isStrictSourceModeEnabled = showSourceModeToggle && sourceMode === 'STRICT';
	const [isPillSelected, setIsPillSelected] = useState(false);
	const onPillOpenChange = useCallback((isOpen: boolean) => setIsPillSelected(isOpen), []);
	const { ghostIconRow, markSourceIconLoaded } = useGhostRenderCoordination({
		filteredApps,
		webSearchEnabled,
		webSearchConfigEnabled,
		shouldRenderGhostIcons,
		isGhostIconsPending,
		ghostIconCount: ghostConnectors.length,
		// Forward to the pill state hook so the operational
		// `ghost_icons_render_blocked` event is emitted alongside the other
		// ghost-icons analytics events (same surface, same actionSubject).
		onRenderBlocked: reportRenderBlocked,
	});

	const filteredAppsIcons = useMemo(() => {
		const tooltipMessage = shouldRenderGhostIcons
			? messages.ghostIconsConnectAppsTooltip
			: messages.filteredAppsTooltip;
		const tooltipContent = disabledTooltip ?? formatMessage(tooltipMessage);
		if (isStrictSourceModeEnabled) {
			return (
				<Tooltip content={tooltipContent}>
					<Box xcss={isPillSelected ? styles.pillContainerSelected : styles.pillContainer}>
						<Inline space="space.050" alignBlock="center">
							<Box xcss={styles.iconItem}>
								<FolderClosedIcon label="" size="small" />
							</Box>
							<Lozenge appearance="discovery">{formatMessage(messages.newBadge)}</Lozenge>
						</Inline>
					</Box>
				</Tooltip>
			);
		}
		const appIcons = filteredApps.map((app) =>
			fg('rovo_chat_use_be_knowledge_icons') ? (
				<Image
					key={app.source}
					src={
						fg('use_new_knowledge_source_icon_url_prefix')
							? app.iconUrl
							: `/gateway/api${app.iconUrl}`
					}
					alt={app.friendlyName}
					width={20}
					height={20}
					onLoad={() => markSourceIconLoaded(app.source)}
					onError={() => markSourceIconLoaded(app.source)}
				/>
			) : (
				<Box key={app.source} xcss={styles.rovoLogoWrapper}>
					<RovoLogoForAppSource
						appSource={app.source}
						rovoLogoProps={{ size: 'xsmall' }}
						fallback={<Box xcss={styles.placeholderLogo} />}
					/>
				</Box>
			),
		);
		const webSourceIcon = RovoLogoFromLogo(GlobeIcon, 'Filter Web result')({ size: 'xsmall' });

		const defaultIcon = RovoLogoFromLogo(PowerCableIcon, 'Sources')({ size: 'xsmall' });
		const ghostIconNodes = buildGhostIconNodes(ghostConnectors);

		const icons =
			webSearchEnabled && webSearchConfigEnabled
				? [webSourceIcon, ...appIcons]
				: appIcons.length > 0
					? appIcons
					: ghostIconRow.isVisible
						? []
						: [defaultIcon];

		const maxVisible = 3;
		const visibleIcons = icons.slice(0, maxVisible);
		const remainingCount = Math.max(0, icons.length - maxVisible);

		return (
			<Tooltip content={tooltipContent}>
				<Box xcss={isPillSelected ? styles.pillContainerSelected : styles.pillContainer}>
					<Inline space="space.050" alignBlock="center">
						{visibleIcons.map((icon, index) => (
							<Box key={index} xcss={styles.iconItem}>
								{icon}
							</Box>
						))}
						{remainingCount > 0 && !ghostIconRow.shouldHideOverflowCount && (
							<Box xcss={styles.remainingCount}>
								<Text size="small" weight="medium">
									+{remainingCount}
								</Text>
							</Box>
						)}
						{ghostIconRow.isVisible && (
							<GhostIconWave
								icons={ghostIconNodes}
								label={formatMessage(messages.ghostIconsLabel)}
								waveCount={shouldAnimate ? 3 : 0}
								iconOpacity={0.5}
							/>
						)}
					</Inline>
				</Box>
			</Tooltip>
		);
	}, [
		filteredApps,
		markSourceIconLoaded,
		webSearchConfigEnabled,
		webSearchEnabled,
		disabledTooltip,
		formatMessage,
		isPillSelected,
		isStrictSourceModeEnabled,
		ghostIconRow,
		ghostConnectors,
		shouldAnimate,
		shouldRenderGhostIcons,
	]);
	const dismissAgentSourceControlSpotlight = useCallback(
		(source?: 'cross-icon' | 'understood-button') => {
			onDismissAgentSourceControlSpotlight?.(source);
		},
		[onDismissAgentSourceControlSpotlight],
	);

	return (
		<ClientSideOnly>
			<FilterByAppsSelectorMenu
				onOpenChange={onPillOpenChange}
				enableAppsDiscoveryUi={enableAppsDiscoveryUi}
				shouldRenderInPortal={shouldRenderInPortal}
				trigger={({ triggerRef, onClick, ...props }) => {
					return (
						<Box xcss={styles.spotlightWrapper} data-spotlight-wrapper>
							<Pressable
								onClick={(e) => {
									if (isDisabled) {
										return;
									}
									handleSourcesPillClick();
									sendAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'pill',
										actionSubjectId: 'filterByAppsSourcesPill',
										attributes: {
											appSources: filteredApps.map((app) => app.friendlyName),
											webSearchEnabled,
										},
									});
									onClick?.(e);
								}}
								{...props}
								ref={triggerRef}
								isDisabled={isDisabled}
								aria-disabled={isDisabled || undefined}
								xcss={isDisabled ? styles.pillPressableDisabled : styles.pillPressable}
								data-spotlight-target="rovo-chat-web-search-spotlight-target"
							>
								{filteredAppsIcons}
							</Pressable>
							<Box id="rovo-chat-web-search-spotlight" xcss={styles.spotlightContainer}>
								{webSearchEnabled &&
									webSearchConfigEnabled &&
									// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
									FeatureGates.getExperimentValue(
										'rovo_web_search_default_on',
										'isEnabled',
										false,
									) && <HeadlessProbeRovoChatSourcesPostOfficePlacement />}
							</Box>
							<AgentSourceControlSpotlight
								isVisible={showAgentSourceControlSpotlight}
								onDismiss={dismissAgentSourceControlSpotlight}
								postOfficeMessage={sourceControlPostOfficeMessage}
							/>
						</Box>
					);
				}}
				orgKnowledge={orgKnowledge}
				assistanceServiceParams={assistanceServiceParams}
				oauthSource={oauthSource}
				oauthSurface={contentContext}
				onAppConnectClick={handleGhostConnectClick}
				onOrgKnowledgeToggle={(updatedOrgKnowledge) => {
					if (updatedOrgKnowledge.filteredApps.length === 0) {
						setOrgKnowledge(chatModeKey, 'disabled');
						onOrgKnowledgeToggle?.(false);
						onFilteredAppsChange?.([]);
					} else {
						setOrgKnowledge(chatModeKey, updatedOrgKnowledge);
						onOrgKnowledgeToggle?.(true);
						onFilteredAppsChange?.(updatedOrgKnowledge.filteredApps);
					}
				}}
				webSearchEnabled={webSearchEnabled}
				onWebSearchToggle={
					webSearchConfigEnabled
						? (enabled) => {
								setWebSearchEnabled(chatModeKey, enabled);
								onWebSearchToggle?.(enabled);
							}
						: undefined
				}
				orgKnowledgeEnabled={orgKnowledge !== 'disabled'}
				onOrgKnowledgeEnabled={(enabled) => {
					if (enabled) {
						setOrgKnowledge(chatModeKey, { filteredApps: allConnectedApps });
						onOrgKnowledgeToggle?.(true);
						onFilteredAppsChange?.(allConnectedApps);
					} else {
						setOrgKnowledge(chatModeKey, 'disabled');
						onOrgKnowledgeToggle?.(false);
						onFilteredAppsChange?.([]);
					}
				}}
				sourceMode={sourceMode}
				onSourceModeChange={(updatedSourceMode) => {
					setSourceMode(chatModeKey, updatedSourceMode);
				}}
				showSourceModeToggle={showSourceModeToggle}
			/>
		</ClientSideOnly>
	);
};

const MENU_FIXED_WIDTH = 360;

const FilterByAppsSelectorMenu = ({
	trigger,
	orgKnowledge,
	assistanceServiceParams,
	onOrgKnowledgeToggle,
	webSearchEnabled,
	onWebSearchToggle,
	orgKnowledgeEnabled,
	onOrgKnowledgeEnabled,
	sourceMode,
	onSourceModeChange,
	onOpenChange,
	oauthSource,
	oauthSurface,
	onAppConnectClick,
	placement,
	enableAppsDiscoveryUi,
	showSourceModeToggle,
	shouldRenderInPortal = false,
}: {
	trigger: ({
		triggerRef,
		onClick,
		...props
	}: CustomTriggerProps<HTMLButtonElement>) => JSX.Element;
	orgKnowledge: OrgKnowledge;
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	onOrgKnowledgeToggle: (orgKnowledge: FiltererdOrgKnowledge) => void;
	webSearchEnabled?: boolean;
	onWebSearchToggle?: (enabled: boolean) => void;
	orgKnowledgeEnabled?: boolean;
	onOrgKnowledgeEnabled?: (enabled: boolean) => void;
	sourceMode?: SourceMode;
	onSourceModeChange?: (sourceMode: SourceMode) => void;
	onOpenChange?: (isOpen: boolean) => void;
	oauthSource?: string;
	oauthSurface?: string;
	onAppConnectClick?: (source: string) => void;
	placement?: Placement;
	/**
	 * When true, the surface opts in to apps discovery UI. The app filter
	 * applies the launched unconnected-apps limit only when the side nav modal
	 * is available. Defaults to off so unaudited surfaces keep the existing
	 * behaviour.
	 */
	enableAppsDiscoveryUi?: boolean;
	showSourceModeToggle?: boolean;
	shouldRenderInPortal?: boolean;
}) => {
	return (
		<DropdownMenu
			shouldRenderToParent={!shouldRenderInPortal}
			placement={placement || 'top-start'}
			trigger={trigger}
			onOpenChange={(attrs) => {
				onOpenChange?.(attrs.isOpen);
			}}
		>
			<MenuGroup maxWidth={MENU_FIXED_WIDTH} minWidth={MENU_FIXED_WIDTH}>
				<Box xcss={styles.popupContainer}>
					<FilterByAppsSelector
						assistanceServiceParams={assistanceServiceParams}
						oauthSource={oauthSource}
						oauthSurface={oauthSurface}
						onAppConnectClick={onAppConnectClick}
						preservedSelectedApps={(orgKnowledge as FiltererdOrgKnowledge).filteredApps ?? []}
						onChange={(apps) => {
							onOrgKnowledgeToggle({ filteredApps: apps });
						}}
						webSearchEnabled={webSearchEnabled}
						onWebSearchToggle={onWebSearchToggle}
						orgKnowledgeEnabled={orgKnowledgeEnabled}
						onOrgKnowledgeToggle={onOrgKnowledgeEnabled}
						sourceMode={sourceMode}
						onSourceModeChange={onSourceModeChange}
						showSourceModeToggle={showSourceModeToggle}
						showGroupedSectionsWithCounts
						enableAppsDiscoveryUi={enableAppsDiscoveryUi}
					/>
				</Box>
			</MenuGroup>
		</DropdownMenu>
	);
};
